import { API, graphqlOperation, Hub } from 'aws-amplify';
import isEqual from 'lodash.isequal';
import { isWithinInterval, parseISO, getUnixTime, startOfWeek, endOfWeek, addSeconds, add, startOfMonth, endOfMonth, isBefore, fromUnixTime, format } from 'date-fns';
import { EventBus, isObjectEmpty, sendNotification, sortArray, generateRandomColor, inicializeDebugLog, handleDebugLog, computeHorsesOverlapFilter, isSameMonth, separateSessionsByWeek, computeHorsesOverlappedSessions, resolveHorseName } from './utils';
import { getSessionFieldByWtype, constructSessionsBag, prepareSessionTransaction, sessionsWithPeriods, constructSessionsToUpdate, updateSessionsBulk, recurringHorseAssignSessions, computeHorsesSessionsToUpdate } from './sessions';
import { checkHorseDailyEffort } from './dataManager';
import * as queries from '@/amplifyGraphql/queries';
import * as mutations from '@/amplifyGraphql/mutations';
import * as subscriptions from '@/amplifyGraphql/subscriptions';
import { presenseStatus } from './variables';
import store from '@/store';
import { preReservationsDeletionDebug } from './request';

const maxRetries = 3;

const subscriptionErrorMessage = 'Un problème de connexion a été détecté, cliquez sur le bouton pour vous reconnecter';

const getSessionIndexById = (uuid, sessions = store.state.sessions) => (sessions || []).findIndex(s => (s.uuid === uuid || s.id === uuid));

function parseSessionJsonFields (session) {
  if (!session) {
    return;
  }
  const sessionObject = {
    ...session,
    participants: session.participants || [],
    instructors: session.instructors || [],
    horses: session.horses || [],
    place: session.place || {},
    metadata: session.metadata || {}
  };

  const fields = ['participants', 'instructors', 'horses', 'metadata'];

  fields.map(field => {
    if (typeof sessionObject[field] === 'string' && sessionObject[field].length) {
      sessionObject[field] = JSON.parse(sessionObject[field]);
    }
  });

  if (!isObjectEmpty(sessionObject.place) && typeof sessionObject.place === 'string') {
    sessionObject.place = JSON.parse(sessionObject.place);
  }

  return sessionObject;
}

function unparseSessionJsonFields (session) {
  const sessionObject = { ...session };

  const jsonFields = ['participants', 'instructors', 'horses', 'place', 'metadata'];

  const objectFields = ['place', 'metadata'];

  jsonFields.map(field => {
    if (session.hasOwnProperty(field)) {
      const defaultValue = objectFields.includes(field) ? {} : [];
      sessionObject[field] = JSON.stringify(sessionObject[field] || defaultValue);
    }
  });

  return sessionObject;
}

async function getSessionById (session_id) {
  return API.graphql(graphqlOperation(queries.getSession, { id: session_id })).then(resp => {
    if (resp) {
      return parseSessionJsonFields(resp.data.getSession);
    }
    return null;
  });
}

const createSession = session => {
  const { selectedDate } = store.getters;
  const { sessions } = store.state;
  const start = startOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
  const end = endOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
  const isBetween = isWithinInterval(new Date(session.start_time * 1000), { start: start, end: end });
  const sessionAlreadyExist = sessions.find(s => s.id === session.id);
  if (isBetween && !sessionAlreadyExist) {
    sessions.push(parseSessionJsonFields(session));
    store.commit('setSessions', sessions);
  }
};

const deleteSession = session => {
  const { sessions } = store.state;

  const sessionsToUpdate = [...sessions];

  const index = sessionsToUpdate.findIndex(s => s.id === session.id);

  if (index !== -1) {
    sessionsToUpdate.splice(index, 1);
    store.commit('setSessions', sessionsToUpdate);
  }
};

const insertMonthlySession = session => {
  const { monthlySessions } = store.state;
  if (!isSameMonth(fromUnixTime(session.start_time))) {
    return;
  }
  const sessionAlreadyExist = monthlySessions.find(s => s.id === session.id);
  if (sessionAlreadyExist) {
    return;
  }
  monthlySessions.push(parseSessionJsonFields(session));
  store.commit('setMonthlySessions', monthlySessions);
};

const updateMonthlySession = async session => {
  const { monthlySessions } = store.state;

  if (!isSameMonth(fromUnixTime(session.start_time))) {
    return;
  }

  const sessions = [...monthlySessions];

  const index = sessions.findIndex(s => s.id === session.id);

  if (index !== -1) {
    sessions[index] = parseSessionJsonFields(session);
  } else {
    sessions.push(parseSessionJsonFields(session));
  }

  store.commit('setMonthlySessions', sessions);
};

const deleteMonthlySession = session => {
  const { monthlySessions } = store.state;

  if (!isSameMonth(fromUnixTime(session.start_time))) {
    return;
  }

  const sessions = [...monthlySessions];

  const index = sessions.findIndex(s => s.id === session.id);

  if (index !== -1) {
    sessions.splice(index, 1);
    store.commit('setMonthlySessions', sessions);
  }
};

async function getSessionsByPeriod (start, end, workspace, results = [], nextToken = null) {
  const filters = { start_time: { gte: start }, end_time: { lte: end } };

  switch (store?.state?.siteInfo?.horanetSource) {
    case 'postgres': {
      filters.codeSiteComptage = { eq: parseInt(store.state.siteInfo.code_site_comptage) };
      break;
    }
    default: {
      filters.workspace = { eq: workspace };
      break;
    }
  }

  const queryArgs = { filter: filters, limit: 1000 };

  if (nextToken) {
    queryArgs.nextToken = nextToken;
  }

  const sessions = await API.graphql(graphqlOperation(queries.searchSessions, queryArgs)).catch(err => {
    console.log('Error getSessionsByPeriod', err);
    return [];
  });

  sessions.data.searchSessions.items.map(session => results.push(parseSessionJsonFields(session)));

  if (sessions.data.searchSessions.nextToken && sessions.data.searchSessions.total > results.length) {
    return getSessionsByPeriod(start, end, workspace, results, sessions.data.searchSessions.nextToken);
  }

  return results;
}

async function searchSessions (filters, nextToken = null, results = []) {
  const response = await API.graphql(graphqlOperation(queries.searchSessions, { filter: filters, limit: 1000, nextToken: nextToken })).catch(err => {
    console.log('Error getSessionsByPeriod', err);
    return { items: [], total: 0, nextToken: null };
  });

  (response.data.searchSessions.items || []).map(s => results.push(parseSessionJsonFields(s)));

  if (response.data.searchSessions.nextToken && response.data.searchSessions.items.length !== response.data.searchSessions.total) {
    return searchSessions(filters, response.data.searchSessions.nextToken, results);
  }

  return results;
}

async function getSessionsByPeriodWithContentType (start, end, workspace, contentType) {
  return searchSessions({ start_time: { gte: start }, end_time: { lte: end }, workspace: { eq: workspace }, [contentType]: { exists: true } });
}

async function weeklySessions (workspace, date) {
  const start = startOfWeek(new Date(date), { weekStartsOn: 1 });
  const end = add(endOfWeek(new Date(date), { weekStartsOn: 1 }), { seconds: 1 });

  const startUnix = getUnixTime(start);
  const endUnix = getUnixTime(end);

  return getSessionsByPeriod(startUnix, endUnix, workspace);
}

const listAmplifySessions = async (workspace = store.getters.amplifyWorkspace, date = store.getters.selectedDate) => {
  const sessions = await weeklySessions(workspace, date);

  /* Upper Planner colors populated from the start */
  const usedColors = ['#9e64f1', '#3b54ff', '#8b8b8b', '#E53935', '#07ad38', '#FB8C00', '#4CAF50', '#FB8C00', '#E53935'];

  const { disciplines } = store.state;

  (disciplines || []).forEach(d => {
    if (!usedColors.includes(d.color)) {
      usedColors.push(d.color);
    }
  });

  /* Lower Planner colors */

  sessions.forEach(s => {
    if (s.color) {
      usedColors.push(s.color);
    } else {
      let generatedColor = generateRandomColor();

      while (usedColors.includes(generatedColor)) {
        generatedColor = generateRandomColor();
      }

      s.color = generatedColor;
      usedColors.push(generatedColor);
    }
  });

  store.commit('setSessions', sessions);
};

const updateSession = async session => {
  // await listAmplifySessions();
  const { sessions } = store.state;

  const sessionsToUpdate = [...sessions];

  const index = sessionsToUpdate.findIndex(s => s.id === session.id);

  if (index !== -1) {
    sessionsToUpdate[index] = parseSessionJsonFields(session);
    store.commit('setSessions', sessionsToUpdate);
  }
};

const insertContentTypeSession = session => {
  const sessions = [...store.state.contentTypeAllSessions];
  const index = sessions.findIndex(s => s.uuid === session.uuid);

  if (index !== -1) {
    return;
  }

  sessions.push(session);
  store.commit('setContentTypeSessions', sessions);

  // only update instructor sessions if the session is in the future or is happening now
  if (isBefore(fromUnixTime(session.end_time), new Date())) {
    return;
  }

  const isIntrustorOnSession = (session.instructors || []).find(i => i.uuid === store.state.user.uuid);

  const instructorSessions = [...store.state.instructorSessions];

  if (isIntrustorOnSession && instructorSessions.some(s => s.id !== session.id)) {
    instructorSessions.push(session);
  }

  store.commit('setInstructorSessions', instructorSessions);
};

const updateContentTypeSession = session => {
  const sessions = [...store.state.contentTypeAllSessions];

  const sessionIndex = getSessionIndexById(session.uuid, sessions);

  // uppdate
  if (sessionIndex !== -1) {
    sessions[sessionIndex] = { ...session };
    store.commit('setContentTypeSessions', sessions);
  }

  // only update instructor sessions if the session is in the future or is happening now
  if (isBefore(fromUnixTime(session.end_time), new Date())) {
    return;
  }

  const instructorSessions = [...store.state.instructorSessions];

  const instructorSessionId = getSessionIndexById(session.uuid, instructorSessions);

  const isIntrustorOnSession = (session.instructors || []).find(i => i.uuid === store.state.user.uuid);

  let haveChanges = false;

  if (instructorSessionId !== -1) {
    if (isIntrustorOnSession) {
      instructorSessions[instructorSessionId] = { ...session };
      store.commit('setInstructorSessions', instructorSessions);
      return;
    }

    instructorSessions.splice(instructorSessionId, 1);
    haveChanges = true;
  }

  if (isIntrustorOnSession && instructorSessions.some(s => s.id !== session.id)) {
    instructorSessions.push(session);
    haveChanges = true;
  }

  if (haveChanges) {
    store.commit('setInstructorSessions', instructorSessions);
  }
};

const deleteContentTypeSession = session => {
  const sessions = [...store.state.contentTypeAllSessions];

  const sessionIndex = getSessionIndexById(session.uuid || session.id, sessions);

  if (sessionIndex !== -1) {
    sessions.splice(sessionIndex, 1);
    store.commit('setContentTypeSessions', sessions);
  }

  // only update instructor sessions if the session is in the future or is happening now
  if (isBefore(fromUnixTime(session.end_time), new Date())) {
    return;
  }

  const instructorSessions = [...store.state.instructorSessions];

  const instructorSessionIndex = getSessionIndexById(session.uuid, instructorSessions);

  if (instructorSessionIndex !== -1) {
    instructorSessions.splice(instructorSessionIndex, 1);
    store.commit('setInstructorSessions', instructorSessions);
  }
};

const addPreReservationToSession = preReservation => {
  const { sessions } = store.state;

  const sessionIndex = sessions.findIndex(s => s.id === preReservation.sessionPre_reserved_participantsId);

  if (sessionIndex !== -1) {
    sessions[sessionIndex].pre_reserved_participants.items.push(preReservation);
    store.commit('setSessions', sessions);
  }
};

const deletePreReservationToSession = preReservation => {
  const { sessions } = store.state;

  const sessionIndex = sessions.findIndex(s => s.id === preReservation.sessionPre_reserved_participantsId);

  if (sessionIndex !== -1) {
    const preReservationIndex = sessions[sessionIndex].pre_reserved_participants.items.find(pr => pr.id === preReservation.id);

    if (preReservationIndex !== -1) {
      sessions[sessionIndex].pre_reserved_participants.items.splice(preReservationIndex, 1);
    }
    store.commit('setSessions', sessions);
  }
};

const deletePreReservationFromStore = data => {
  const sessions = [...store.state.sessions];
  const deletedPreReservations = JSON.parse(data);

  deletedPreReservations.items.map(itemID => {
    const index = sessions.findIndex(session => session.pre_reserved_participants.items.some(preReservation => itemID === preReservation.id));

    if (index !== -1) {
      const index2 = sessions[index].pre_reserved_participants.items.findIndex(i => i.id === itemID);

      if (index2 !== -1) {
        sessions[index].pre_reserved_participants.items.splice(index2, 1);
      }
    }
  });
  store.commit('setSessions', sessions);
};

const populateAvailableHorse = (session, action) => {
  const { selectedSession } = store.state;
  if (selectedSession) {
    const isOverlap = isWithinInterval(new Date(session.start_time * 1000), { start: new Date(selectedSession.start_time * 1000), end: addSeconds(new Date(selectedSession.end_time * 1000), -10) });

    if (isOverlap) {
      const horses = session.horses || [];

      const available = [...store.state.availableHorses];

      if (action === 'delete') {
        horses.map(h => available.push(h));
        store.commit('setAvailableHorses', sortArray(available, 'horse_name'));
        return;
      }
      // CREATE UPDATE

      const allHorses = store.state.horses;
      let indexToRemove = [];

      allHorses.map(h => {
        if (!horses.find(hor => hor.uuid === h.uuid) && !available.find(hor => hor.uuid === h.uuid)) {
          available.push(h);
        }

        if (horses.find(hor => hor.uuid === h.uuid) && available.find(hor => hor.uuid === h.uuid)) {
          const index = available.findIndex(hor => hor.uuid === h.uuid);
          indexToRemove.push(index);
        }
      });

      if (indexToRemove && indexToRemove.length) {
        indexToRemove = indexToRemove.sort((a, b) => (a < b ? -1 : 1));
        for (let i = indexToRemove.length - 1; i >= 0; i -= 1) {
          available.splice(indexToRemove[i], 1);
        }
      }

      store.commit('setAvailableHorses', sortArray(available, 'horse_name'));
    }
  }
};

const handleTransactionResponse = data => {
  const sessionsToCreate = data.create;
  const sessionsToDelete = data.delete;
  const sessionsToEdit = data.edit;

  const { sessions } = store.state;

  const sessionsToUpdate = [...sessions];

  sessionsToCreate.map(session => {
    if (session.workspace === store.getters.amplifyWorkspace) {
      const index = sessionsToUpdate.findIndex(s => s.id === session.id);
      if (index !== -1) {
        sessionsToUpdate[index] = {
          ...sessionsToUpdate[index],
          ...parseSessionJsonFields(session)
        };
        return;
      }
      const { selectedDate } = store.getters;
      const start = startOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
      const end = endOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
      const isBetween = isWithinInterval(new Date(session.start_time * 1000), { start: start, end: end });
      if (isBetween) {
        sessionsToUpdate.push(parseSessionJsonFields(session));
      }
    }
  });

  sessionsToDelete.map(session => {
    const index = sessionsToUpdate.findIndex(s => s.id === session.id);

    if (index !== -1) {
      sessionsToUpdate.splice(index, 1);
    }
  });

  sessionsToEdit.map(session => {
    const index = sessionsToUpdate.findIndex(s => s.id === session.id);

    if (index !== -1) {
      sessionsToUpdate[index] = {
        ...sessionsToUpdate[index],
        ...parseSessionJsonFields(session)
      };
    }
  });

  store.commit('setSessions', sessionsToUpdate);
};

const handleMonthlySessionsTransaction = data => {
  const sessionsToCreate = data.create;
  const sessionsToDelete = data.delete;
  const sessionsToEdit = data.edit;

  const { monthlySessions } = store.state;

  const sessionsToUpdate = [...monthlySessions];

  [...sessionsToCreate, ...sessionsToEdit].map(session => {
    if (session.workspace === store.getters.amplifyWorkspace) {
      const index = sessionsToUpdate.findIndex(s => s.id === session.id);
      if (index !== -1) {
        sessionsToUpdate[index] = {
          ...sessionsToUpdate[index],
          ...parseSessionJsonFields(session)
        };
        return;
      }
      const { selectedDate } = store.getters;
      const start = startOfMonth(parseISO(selectedDate));
      const end = endOfMonth(parseISO(selectedDate));
      const isBetween = isWithinInterval(new Date(session.start_time * 1000), { start: start, end: end });
      if (isBetween) {
        sessionsToUpdate.push(parseSessionJsonFields(session));
      }
    }
  });

  sessionsToDelete.map(session => {
    const index = sessionsToUpdate.findIndex(s => s.id === session.id);

    if (index !== -1) {
      sessionsToUpdate.splice(index, 1);
    }
  });

  store.commit('setMonthlySessions', sessionsToUpdate);
};

const handleContentTypeSession = data => {
  const sessionsToCreate = data.create;
  const sessionsToDelete = data.delete;
  const sessionsToEdit = data.edit;

  sessionsToCreate.map(session => {
    const parseSessions = parseSessionJsonFields(session);
    insertContentTypeSession(parseSessions);
    populateAvailableHorse(parseSessions);
  });

  sessionsToEdit.map(session => {
    const parseSessions = parseSessionJsonFields(session);
    updateContentTypeSession(parseSessions);
    populateAvailableHorse(parseSessions);
  });

  sessionsToDelete.map(session => {
    deleteContentTypeSession(session);
    const parseSessions = parseSessionJsonFields(session);
    populateAvailableHorse(parseSessions, 'delete');
  });
};

const handlePreReservationTransaction = items => {
  let sessions = [...store.state.sessions];

  items.map(item => {
    switch (item.action) {
      case 'ADD_PRE_RESERVATION': {
        const sessionIndex = sessions.findIndex(s => s.id === item.item.sessionPre_reserved_participantsId);

        if (sessionIndex !== -1) {
          sessions[sessionIndex].pre_reserved_participants.items.push(item.item);
        }
        break;
      }
      case 'REMOVE_PRE_RESERVATION': {
        sessions = sessions.map(session => {
          const preReservationIndex = session.pre_reserved_participants.items.findIndex(preReservation => preReservation.id === item.item);
          if (preReservationIndex !== -1) {
            session.pre_reserved_participants.items.splice(preReservationIndex, 1);
          }
          return session;
        });
        break;
      }
      case 'ADD_RESERVATION': {
        const sessionIndex = sessions.findIndex(s => s.id === item.item.sessionId);

        if (sessionIndex !== -1) {
          item.item.participants.map(participant => {
            if (!sessions[sessionIndex].participants) {
              sessions[sessionIndex].participants = [];
            }
            const isParticipantonSession = sessions[sessionIndex].participants.some(p => p.uuid === participant.uuid);

            if (!isParticipantonSession) {
              sessions[sessionIndex].participants.push(participant);
            }

            if (participant.horse) {
              if (!sessions[sessionIndex].horses) {
                sessions[sessionIndex].horses = [];
              }
              const horseIndex = sessions[sessionIndex].horses.findIndex(h => h.uuid === participant.horse);
              if (horseIndex === -1) {
                sessions[sessionIndex].horses.push({ uuid: participant.horse, wtitle: resolveHorseName(participant.horse) });
              }
            }
          });
        }
        break;
      }
      default:
    }
  });

  store.commit('setSessions', sessions);
};

const handleSessionCreateEdit = session => {
  // if on store update if not create
  const storeSessions = [...store.state.sessions];
  const { siteInfo } = store.state;

  if (session && session.codeSiteComptage && siteInfo.code_site_comptage && session.codeSiteComptage.toString() === siteInfo.code_site_comptage.toString()) {
    const index = storeSessions.findIndex(s => s.id === session.id);
    const fieldsToIgnore = ['status', 'updatedAt', 'createdAt'];

    const parsedSession = parseSessionJsonFields(session);

    if (index !== -1) {
      Object.keys(parsedSession).map(key => {
        if (fieldsToIgnore.includes(key)) {
          delete parsedSession[key];
        }
      });

      storeSessions[index] = {
        ...storeSessions[index],
        ...parsedSession
      };
      store.commit('setSessions', storeSessions);
      return;
    }

    const { selectedDate } = store.getters;
    const start = startOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
    const end = endOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
    const isBetween = isWithinInterval(new Date(session.start_time * 1000), { start: start, end: end });
    if (isBetween) {
      storeSessions.push(parsedSession);
    }
  }

  store.commit('setSessions', storeSessions);
};

const deleteParticipantsFromSession = session => {
  const storeSessions = [...store.state.sessions];
  let hasChanges = false;

  if (session) {
    const index = storeSessions.findIndex(s => s.id === session.id);

    if (index === -1) return;

    const storeSession = storeSessions[index];

    const sessionParticipants = session.participants ? JSON.parse(session.participants) : [];

    // Add new participants
    sessionParticipants.forEach(participant => {
      if (!storeSession.participants.some(p => p.uuid === participant.uuid)) {
        storeSession.participants.push(participant);
      }
    });

    // Remove participants that are no longer present
    storeSession.participants = storeSession.participants.filter(participant => sessionParticipants.some(p => p.uuid === participant.uuid));

    hasChanges = true;
  }

  if (hasChanges) {
    store.commit('setSessions', storeSessions);
  }
};

const addParticipantToSessionSubscription = session => {
  const storeSessions = [...store.state.sessions];

  let haveChanges = false;

  const index = storeSessions.findIndex(s => s.id === session?.id);

  if (index !== -1) {
    haveChanges = true;
    session.participants.map(participant => {
      const participantIndex = storeSessions[index].participants.findIndex(p => p.uuid === participant.uuid);

      if (participantIndex !== -1) {
        storeSessions[index].participants[participantIndex] = {
          ...storeSessions[index].participants[participantIndex],
          ...participant
        };
        return;
      }
      storeSessions[index].participants.push(participant);

      if (participant.horse) {
        const horseIndex = storeSessions[index].horses.findIndex(h => h.uuid === participant.horse);
        if (horseIndex === -1) {
          storeSessions[index].horses.push({ uuid: participant.horse, wtitle: resolveHorseName(participant.horse) });
        }
      }
    });
  }

  if (haveChanges) {
    store.commit('setSessions', storeSessions);
  }
};

const handleSubscription = (retry, workspace = store.getters.amplifyWorkspace) => {
  const { siteInfo } = store.state;

  const handleSubscriptionError = error => {
    console.warn(error);
    const nextRetry = retry + 1;
    if (nextRetry <= maxRetries) {
      handleSubscription(nextRetry, workspace);
      return;
    }
    store.commit('setWebSocketError', subscriptionErrorMessage);
  };

  const subs = {
    create: null,
    delete: null,
    update: null,
    createPreReservation: null,
    deletePreReservation: null,
    replicaSessions: null,
    transactionPreReservation: null,
    removePreReservations: null,
    handleSessionCreateEdit: null,
    deleteParticipantsFromSession: null,
    addParticipantToSession: null
  };

  const filter = {};

  if (siteInfo && ['postgres'].includes(siteInfo.horanetSource)) {
    filter.codeSiteComptage = { eq: parseInt(siteInfo.code_site_comptage) };
  } else {
    filter.workspace = { eq: workspace };
  }

  const startDate = parseFloat(format(startOfWeek(new Date(store.getters.selectedDate), { weekStartsOn: 1 }), 't'));
  const endDate = parseFloat(format(endOfWeek(new Date(store.getters.selectedDate), { weekStartsOn: 1 }), 't'));

  filter.start_time = { between: [startDate, endDate] };

  subs.create = API.graphql(
    graphqlOperation(subscriptions.onCreateSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      const session = value.data.onCreateSession;
      const parseSessions = parseSessionJsonFields(session);
      createSession(parseSessions);
      insertMonthlySession(parseSessions);
      insertContentTypeSession(parseSessions);
      populateAvailableHorse(parseSessions);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.update = API.graphql(
    graphqlOperation(subscriptions.onUpdateSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      const session = value.data.onUpdateSession;
      const parseSessions = parseSessionJsonFields(session);
      updateContentTypeSession(parseSessions);
      updateSession(parseSessions);
      updateMonthlySession(parseSessions);
      populateAvailableHorse(parseSessions);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.delete = API.graphql(
    graphqlOperation(subscriptions.onDeleteSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      const session = value.data.onDeleteSession;
      deleteContentTypeSession(session);
      deleteSession(session);
      deleteMonthlySession(session);
      const parseSessions = parseSessionJsonFields(session);
      populateAvailableHorse(parseSessions, 'delete');
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.createPreReservation = API.graphql(
    graphqlOperation(subscriptions.onCreatePreReservedParticipant, { filter: { workspace: { eq: workspace } } })
  ).subscribe({
    next: ({ provider, value }) => {
      addPreReservationToSession(value.data.onCreatePreReservedParticipant);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.deletePreReservation = API.graphql(
    graphqlOperation(subscriptions.onDeletePreReservedParticipant, { filter: { workspace: { eq: workspace } } })
  ).subscribe({
    next: ({ provider, value }) => {
      deletePreReservationToSession(value.data.onDeletePreReservedParticipant);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.removePreReservations = API.graphql(
    graphqlOperation(subscriptions.onRemovePreReservations)
  ).subscribe({
    next: ({ provider, value }) => {
      const { data } = value.data.onRemovePreReservations;
      if (data) {
        deletePreReservationFromStore(data);
      }
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.replicaSessions = API.graphql(
    graphqlOperation(subscriptions.onHandleUpdateReplicaSessions, { filter: { workspace: { eq: workspace } } })
  ).subscribe({
    next: ({ provider, value }) => {
      handleContentTypeSession(value.data.onHandleUpdateReplicaSessions);
      handleTransactionResponse(value.data.onHandleUpdateReplicaSessions);
      handleMonthlySessionsTransaction(value.data.onHandleUpdateReplicaSessions);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.transactionPreReservation = API.graphql(
    graphqlOperation(subscriptions.onHandlePreReservations)
  ).subscribe({
    next: ({ provider, value }) => {
      const { success, data } = value.data.onHandlePreReservations;

      if (success) {
        const jsonData = JSON.parse(data);
        handlePreReservationTransaction(jsonData.items);
      }
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.handleSessionCreateEdit = API.graphql(
    graphqlOperation(subscriptions.onHandleCreateEditSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      handleSessionCreateEdit(value.data.onHandleCreateEditSession);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.deleteParticipantsFromSession = API.graphql(
    graphqlOperation(subscriptions.onDeleteParticipantFromSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      deleteParticipantsFromSession(value.data.onDeleteParticipantFromSession);
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  subs.addParticipantToSession = API.graphql(
    graphqlOperation(subscriptions.onAddParticipantToSession, { filter: filter })
  ).subscribe({
    next: ({ provider, value }) => {
      const session = value.data.onAddParticipantToSession;
      if (session) {
        const jsonData = parseSessionJsonFields(session);
        addParticipantToSessionSubscription(jsonData);
      }
    },
    error: error => {
      handleSubscriptionError(error);
    }
  });

  store.commit('setAmplifySubscription', subs);

  return Object.values(subs).every(value => !!value);
};

const unsubscribe = async () => {
  const { amplifySubscription } = store.state;

  if (amplifySubscription) {
    Object.values(amplifySubscription).map(subscription => {
      subscription.unsubscribe();
    });

    store.commit('setAmplifySubscription', null);
  }
};

const deleteSessionHelpers = session => {
  const sessionObject = { ...session };
  // delete helpers
  delete sessionObject.style;
  delete sessionObject.color;
  // delete sessionObject.id;
  delete sessionObject.selectedTimes;
  delete sessionObject.end_date;
  delete sessionObject.date;
  delete sessionObject.form;
  delete sessionObject.date;
  delete sessionObject.original_start_time;
  delete sessionObject.original_end_time;
  delete sessionObject.createdAt;
  delete sessionObject.updatedAt;
  delete sessionObject._deleted;
  delete sessionObject._lastChangedAt;
  delete sessionObject._version;
  delete sessionObject.oldUuid;
  delete sessionObject.participantsAmount;

  return sessionObject;
};

const createSessionAmplify = async session => {
  let sessionObject = {
    ...session,
    start_time: session.original_start_time || session.start_time,
    end_time: session.original_end_time || session.end_time,
    workspace: store.getters.amplifyWorkspace
  };

  // delete helpers
  sessionObject = deleteSessionHelpers(sessionObject);

  const { user } = store.state;

  sessionObject.metadata = {
    createTime: getUnixTime(new Date()),
    createUserName: user ? user.uuid : null,
    createUserEmail: user ? user.email : null,
    lastUpdateTime: getUnixTime(new Date()),
    lastUpdateUserName: user ? user.uuid : null,
    lastUpdateUserEmail: user ? user.email : null
  };

  sessionObject = unparseSessionJsonFields(sessionObject);
  const { pre_reserved_participants, ...sessionInput } = sessionObject;

  if (session) {
    delete sessionInput.id;
    return API.graphql(graphqlOperation(mutations.createSession, { input: sessionInput }))
      .then(resp => {
        const text = 'Session créée avec succès';
        sendNotification('success', text);
      })
      .catch(err => {
        console.log('CREATE ERROR ', err);
      });
  }
};

const updateSessionAmplify = async session => {
  let sessionObject = deleteSessionHelpers(session);
  // const original = await DataStore.query(Session, sessionObject.id);

  // if (original) {
  //   await DataStore.save(Session.copyOf(original, item => {
  //     Object.entries(sessionObject).map(([key, value]) => {
  //       item[key] = value;
  //     });
  //   }));
  // }
  const sessionExist = await getSessionById(session.id);

  if (!sessionExist) {
    sendNotification('error', 'erreur lors de la mise à jour de la session');
    return;
  }

  let debugLog = null;
  const { user } = store.state;

  sessionObject.metadata = {
    ...sessionObject.metadata,
    lastUpdateTime: getUnixTime(new Date()),
    lastUpdateUserName: user ? user.uuid : null,
    lastUpdateUserEmail: user ? user.email : null
  };

  sessionObject = unparseSessionJsonFields(sessionObject);

  const { pre_reserved_participants, ...newSessionObject } = sessionObject;

  if (store.state.debugMode) {
    debugLog = inicializeDebugLog({ newSessionObject, method: 'mutations.updateSession' });
  }

  // eslint-disable-next-line consistent-return
  return API.graphql(graphqlOperation(mutations.updateSession, { input: newSessionObject }))
    .then(resp => {
      if (store.state.debugMode && debugLog) {
        debugLog.response = resp;
      }
      return resp;
    })
    .catch(err => {
      if (store.state.debugMode) {
        debugLog.error = err;
      }
      console.log('Err updateSession', err);
    }).finally(() => {
      if (store.state.debugMode) {
        handleDebugLog(debugLog);
      }
    });
};

// on this method we will need to delete proprieties from amplify session that doesn't exist on horanet session
const compareSessions = (amplifySession, horanetSession) => {
  const objToCompare = { ...amplifySession };

  horanetSession.start_time = horanetSession.original_start_time;
  horanetSession.end_time = horanetSession.original_end_time;

  const horanetSessionObj = { ...horanetSession };

  const horanetSessionFieldToDelete = ['original_end_time', 'original_start_time', 'uuid', 'participants'];
  horanetSessionFieldToDelete.forEach(field => {
    delete horanetSessionObj[field];
  });

  const allFieldEqual = Object.keys(horanetSessionObj).map(key => isEqual(objToCompare[key], horanetSessionObj[key]));

  return allFieldEqual.every(field => field);
};

const syncHoranetSessions = async horanetSessions => {
  const promises = horanetSessions.map(async session => {
    const sessionObject = {
      ...session,
      id: session.uuid
    };

    const response = await API.graphql(graphqlOperation(queries.getSessionByHoranetId, { horanet_id: sessionObject.horanet_id, workspace: store.getters.amplifyWorkspace })).catch(async err => {
      console.log('ERR getSessionByHoranetId', err);
    });

    if (response && !response.data.getSessionByHoranetId) {
      await createSessionAmplify(sessionObject);
      return sessionObject;
    }

    if (response) {
      const isSessionEqual = compareSessions(response.data.getSessionByHoranetId, session);
      // TODO: improve
      const participants = session.participants || [];
      const horanetIds = participants.map(p => p.horanet_id);
      const parseSessions = parseSessionJsonFields(response.data.getSessionByHoranetId);

      parseSessions.participants.map(p => {
        if (!horanetIds.includes(p.horanet_id)) {
          participants.push(p);
        }
      });

      const update = !session.participants.every(p => parseSessions.participants.map(part => part.horanet_id).includes(p.horanet_id));
      if (!isSessionEqual || update) {
        await updateSessionAmplify({
          ...response.data.getSessionByHoranetId,
          ...session,
          participants: participants
        });
      }
    }
  });
  return Promise.all(promises);
};

const handleSessions = async (subscriveToTopic = true) => {
  await store.dispatch('getDisciplines');
  await listAmplifySessions();

  if (subscriveToTopic) {
    await unsubscribe();
    handleSubscription(0);
  }
};

const getRecurringSession = async recurring_uuid => {
  const filters = {
    recurring_uuid: { eq: recurring_uuid },
    workspace: { eq: store.getters.amplifyWorkspace }
  };
  return API.graphql(graphqlOperation(queries.searchSessions, { filter: filters })).then(resp => {
    if (resp) {
      return resp.data.searchSessions.items.map(s => parseSessionJsonFields(s));
    }

    return [];
  });
};

const addContentTypeToSession = async (session, body, sessionField) => {
  const sessionBody = { ...session };
  if (sessionField === 'place') {
    sessionBody[sessionField] = {
      ...sessionBody[sessionField],
      ...body
    };
  } else {
    const contentTypeData = [...sessionBody[sessionField]];
    contentTypeData.push(body);
    sessionBody[sessionField] = contentTypeData;
  }

  store.commit('setLoading', true);
  await updateSessionAmplify(sessionBody);
  store.commit('setLoading', false);
  const text = `${body.wtitle} ajoutée à ${sessionBody.name}`;
  sendNotification('success', text);
};

const prepareSession = async ({ uuid, session, values, type, isMultipleValue }, option = 'SINGLE') => {
  const sessionToUpdate = { ...session };
  const contentTypeValue = values.find(val => val.uuid === uuid);
  const content = {
    uuid: contentTypeValue.uuid,
    wtitle: contentTypeValue.wtitle || contentTypeValue.fullname
  };

  if (contentTypeValue.wtype === 'place') {
    if (!sessionToUpdate[type]) {
      sessionToUpdate[type] = {};
    }
  } else if (!sessionToUpdate[type]) {
    sessionToUpdate[type] = [];
  }

  const sessionsToUpdate = [
    { ...sessionToUpdate }
  ];

  if (sessionToUpdate.recurring && option === 'ALL') {
    const recurringSessions = await getRecurringSession(sessionToUpdate.recurring_uuid);

    recurringSessions.forEach(s => {
      const sess = { ...s };
      if (!sessionsToUpdate.map(se => se.uuid).includes(sess.uuid)) {
        if (contentTypeValue.wtype === 'place') {
          if (!sess[type]) {
            sess[type] = {};
          }
        } else if (!sess[type]) {
          sess[type] = [];
        }
        sessionsToUpdate.push(sess);
      }
    });
  }

  const canUpdateSessions = sessionsToUpdate.map(s => {
    // default -> not multiple if session already have one sessionHave content = true
    let sessionHaveContent = null;
    if (contentTypeValue.wtype === 'place') {
      sessionHaveContent = !isObjectEmpty(s[type]) ? !!s[type].uuid : false;
    } else {
      sessionHaveContent = s[type] && s[type].length;
    }
    if (isMultipleValue) {
      // if is multiple verify if the content already exist on the content
      sessionHaveContent = s[type].map(ct => ct.uuid).find(key => key === uuid);
    }

    return !sessionHaveContent;
  });

  if (!canUpdateSessions.every(ses => ses)) {
    const text = `${content.wtitle} ne peut pas être ajoutée à la ${sessionToUpdate.name}`;
    sendNotification('error', text);
    return;
  }

  const promises = sessionsToUpdate.map(s => {
    addContentTypeToSession(s, content, type);
  });

  await Promise.all(promises);
};

const updateSessionContentType = (uuid, session, values, type, isMultipleValue, wtype) => {
  const ignoreRecurringOption = ['horse'];
  if (session.recurring && !ignoreRecurringOption.includes(wtype)) {
    EventBus.$emit('showDragAndDropModal', { uuid, session, values, type, isMultipleValue });
    return;
  }

  prepareSession({ uuid, session, values, type, isMultipleValue });
};

async function updateSessionPresence ({ participant, session, status, order_uuid = null }) {
  const { isEditing, ...sessionBody } = session;
  const index = sessionBody.participants.findIndex(p => p.uuid === participant.uuid);

  if (index !== -1) {
    sessionBody.participants[index].presence = status === presenseStatus.PRESENT;
    sessionBody.participants[index].presence_status = status;

    if (status === presenseStatus.ABSENT) {
      sessionBody.participants[index].horse = null;
    }

    const sessionToSent = { ...sessionBody };

    // delete helpers
    sessionBody.participants.map(p => {
      delete p.participantBody;
      delete p.fullname;
    });

    // update session on amplify
    await updateSessionAmplify(sessionBody);
    return { success: true, participants: sessionToSent.participants };
  }
  return { success: false };
}

async function getAssociateRecurringSessionsByDate (session, startTime, endTime) {
  if (!session.recurring_uuid) {
    return [];
  }
  const filters = {
    start_time: { gte: startTime },
    end_time: { lte: endTime },
    recurring_uuid: { eq: session.recurring_uuid },
    workspace: { eq: store.getters.amplifyWorkspace }
  };

  return API.graphql(graphqlOperation(queries.searchSessions, { filter: filters })).then(resp => {
    if (resp) {
      return resp.data.searchSessions.items.map(s => parseSessionJsonFields(s));
    }
    return [];
  });
}

async function getAssociateRecurringSessions (session) {
  if (!session.recurring_uuid) {
    return [];
  }
  const filters = {
    recurring_uuid: { eq: session.recurring_uuid },
    workspace: { eq: store.getters.amplifyWorkspace }
  };

  return API.graphql(graphqlOperation(queries.searchSessions, { filter: filters })).then(resp => {
    if (resp) {
      return resp.data.searchSessions.items.map(s => parseSessionJsonFields(s));
    }
    return [];
  }).catch(err => {
    console.log('ERROR getAssociateRecurringSessions ', err);
    return [];
  });
}

async function contentTypeSessions (contentType, startTime = null, endTime = null, limit = null, nextToken = null, results = []) {
  if (!contentType) {
    return [];
  }

  const queryLimit = limit || 1000;

  const sessionField = getSessionFieldByWtype(contentType.wtype);

  const { amplifyWorkspace } = store.getters;
  const response = await API.graphql(graphqlOperation(queries.getContentTypeSessions, { content_type: sessionField, content_type_uuid: contentType.uuid, start: startTime, end: endTime, workspace: amplifyWorkspace, limit: queryLimit, nextToken })).catch(err => {
    console.log('ERR getContentTypeSessions', err);
    return { data: { getContentTypeSessions: null } };
  });
  if (response.data.getContentTypeSessions && response.data.getContentTypeSessions.items) {
    (response.data.getContentTypeSessions.items || []).map(session => results.push(parseSessionJsonFields(session)));
    if (limit && limit <= 1000) {
      return results;
    }

    const responseNextToken = response.data.getContentTypeSessions.nextToken;
    const { total } = response.data.getContentTypeSessions;

    if (!responseNextToken || total < queryLimit) {
      return results;
    }

    const newLimit = limit ? limit - total : null;

    return contentTypeSessions(contentType, startTime, endTime, newLimit, responseNextToken, results);
  }

  return [];
}

const getSameHoranetSessions = async session => {
  const filters = {
    activity_code: { eq: session.activity_code },
    workspace: { eq: store.getters.amplifyWorkspace }
  };

  return API.graphql(graphqlOperation(queries.searchSessions, { filter: filters })).then(resp => {
    if (resp) {
      return resp.data.searchSessions.items.map(s => parseSessionJsonFields(s));
    }
    return [];
  });
};

async function updateReplicaSessions (body, workspace = store.getters.amplifyWorkspace, recurringUuid = null) {
  let debugLog = null;
  const maxItem = body.create.length + body.edit.length + body.delete.length;

  if (maxItem > 100) {
    return { success: false, message: 'Je ne peux pas gérer plus de 100 sessions à gérer' };
  }

  if (store.state.debugMode) {
    debugLog = inicializeDebugLog({ body, workspace, recurringUuid, method: 'mutations.handleUpdateReplicaSessions' });
  }

  return API.graphql(graphqlOperation(mutations.handleUpdateReplicaSessions, { input: body, workspace, recurringUuid }))
    .then(resp => {
      const response = resp.data.handleUpdateReplicaSessions;
      const success = !!response.create.length || !!response.delete.length || !!response.edit.length;
      if (store.state.debugMode && debugLog) {
        debugLog.response = response;
      }

      if (response.create && response.create.length) {
        EventBus.$emit('createdSessions', response.create.map(s => parseSessionJsonFields(s)));
      }
      if (response.edit && response.edit.length) {
        EventBus.$emit('editedSessions', response.edit.map(s => parseSessionJsonFields(s)));
      }
      if (response.delete && response.delete.length) {
        EventBus.$emit('deletedSessions', response.delete);
      }

      return { success: success, message: success ? 'Toutes les sessions mises à jour' : 'Erreur lors de l\'appel de la mutation' };
    })
    .catch(err => {
      if (store.state.debugMode) {
        debugLog.error = err;
      }
      return { success: false, message: 'quelque chose s\'est mal passé' };
    })
    .finally(() => {
      if (store.state.debugMode) {
        handleDebugLog(debugLog);
      }
    });
}

async function openSearchCustomSessions (filter, limit = 100) {
  if (!filter) {
    return [];
  }

  return API.graphql(graphqlOperation(queries.customSessionSearch, { query: filter, limit }))
    .then(resp => {
      if (resp && resp.data && resp.data.customSessionSearch) {
        return resp.data.customSessionSearch.items.map(s => parseSessionJsonFields(s));
      }
      return [];
    })
    .catch(err => {
      console.log('Error in customSessionSearch:', err);
      return [];
    });
}

const deleteContentsFromSession = async (session, uuid, updateType, contentType) => {
  let sessions = [session];
  if (session.recurring && (session.replica || sessionsWithPeriods.includes(session.type))) {
    sessions = await constructSessionsBag(updateType, session);
  }

  const contentTypes = [contentType];

  switch (contentType) {
    case 'horses':
      sessions = sessions.map(s => {
        const horsesIndex = (s.horses || []).findIndex(h => h.uuid === uuid);

        if (horsesIndex !== -1) {
          s.horses.splice(horsesIndex, 1);

          const participantHorseIndex = (s.participants || []).findIndex(p => p.horse && p.horse === uuid);

          if (participantHorseIndex !== -1) {
            s.participants[participantHorseIndex].horse = null;
            contentTypes.push('participants');
            return s;
          }

          const instructorHorseIndex = (s.instructors || []).findIndex(i => i.horse && i.horse === uuid);

          if (instructorHorseIndex !== -1) {
            s.instructors[instructorHorseIndex].horse = null;
            contentTypes.push('instructors');
          }
        }

        return s;
      });
      break;

    default:
      sessions = sessions.map(s => {
        const contentIndex = (s[contentType] || []).findIndex(c => c.uuid === uuid);

        if (contentIndex !== -1) {
          s[contentType].splice(contentIndex, 1);
        }

        return s;
      });
      break;
  }

  if (sessions.length) {
    return updateSessionsBulk(sessions, contentTypes);
  }
  return false;
};

const addParticipantToSession = async (session_id, participant, updateType = null, endDate = null) => {
  const session = await getSessionById(session_id);

  if (!session) {
    sendNotification('error', 'erreur lors de la mise à jour de la session');
    return;
  }

  let sessions = [session];
  if (session.recurring && (session.replica || sessionsWithPeriods.includes(session.type))) {
    if (updateType) {
      sessions = await constructSessionsBag(updateType, session, undefined, endDate);
    }
  }

  const sessionsToUpdate = [];

  sessions.map(sess => {
    if ((sess.participants || []).some(p => p.uuid === participant.uuid)) {
      sendNotification('error', 'Participant déjà à la session');
      return;
    }
    const participants = (sess.participants || []);

    const { horse, ...participantBody } = participant;

    if (sess.id === session_id) {
      participantBody.horse = horse;
    }

    participants.push(participantBody);

    sessionsToUpdate.push({ ...sess, participants });
  });

  const body = {
    create: [],
    edit: [],
    delete: []
  };

  let recurringUuid = null;

  body.edit = sessionsToUpdate.map(s => {
    recurringUuid = s.recurring_uuid;
    return prepareSessionTransaction({ id: s.id, participants: s.participants });
  });

  const response = await updateReplicaSessions(body, store.getters.amplifyWorkspace, recurringUuid);

  if (response) {
    sendNotification(response.success ? 'success' : 'error', response.message);
    return;
  }
  sendNotification('error', 'Quelque chose s\'est mal passé');
};

const addInstructorsToSession = async (session_id, instructors, updateType = null, endDate = null) => {
  const sessions = await constructSessionsToUpdate(session_id, updateType, endDate);

  if (!sessions || !sessions.length) {
    return false;
  }

  const sessionsToUpdate = [];

  sessions.map(sess => {
    const sessionInstructors = ([...sess.instructors] || []);
    instructors.map(instructor => {
      const instructorIndex = sessionInstructors.findIndex(i => i.uuid === instructor.uuid);

      if (instructorIndex !== -1) {
        return;
      }

      const { horse, ...instructorBody } = instructor;

      if (sess.id === session_id) {
        instructorBody.horse = horse;
      }

      sessionInstructors.push({ uuid: instructorBody.uuid, wtitle: instructorBody.wtitle || instructorBody.label, horse: instructorBody.horse });
    });

    if ((sessionInstructors || []).length !== (sess.instructors || []).length) {
      sessionsToUpdate.push({ ...sess, instructors: sessionInstructors });
    }
  });
  if (sessionsToUpdate.length) {
    return updateSessionsBulk(sessionsToUpdate, ['instructors']);
  }
  return false;
};

/* When ownerParticipant is provided we are only adding one horse to the session */
const addHorsesToSession = async (session_id, horses, updateType = null, endDate = null, isInstructor = false, effortCallback = false, ownerParticipant = null, sessionCopy = false) => {
  const sessions = await constructSessionsToUpdate(session_id, updateType, endDate);

  if (!sessions || !sessions.length) {
    return false;
  }

  if (!effortCallback) {
    const checkForHorseEffort = await checkHorseDailyEffort(horses, sessions);
    if (Object.keys(checkForHorseEffort)?.length) {
      store.commit('setAlertEffort', checkForHorseEffort);
      store.commit('setShowMaxEffortModal', true);
      store.commit('setMaxEffortCallback', () => addHorsesToSession(session_id, horses, updateType, endDate, isInstructor, true, ownerParticipant));
      return false;
    }
  }

  const session = sessions.find(s => s.id === session_id);
  const recurringUuid = session?.recurring_uuid || null;
  let contentTypes = ['horses'];

  if ((ownerParticipant && horses.some(horse => (horse.owners || []).some(owner => owner.ownerUuid === ownerParticipant.uuid))) || recurringHorseAssignSessions.includes(session.type) || sessionCopy) {
    const horsesUuids = horses.map(h => h.uuid);
    const { selectedDate } = store.getters;
    const { sessionsThisWeek, sessionsNotThisWeek } = separateSessionsByWeek(sessions, selectedDate);

    const overlappedSessions = await computeHorsesOverlappedSessions(horsesUuids, sessionsThisWeek, sessionsNotThisWeek, recurringUuid);

    if (overlappedSessions?.length) {
      const { modifiedSessions, aditionalContentTypes } = computeHorsesSessionsToUpdate(overlappedSessions, sessions, horses, ownerParticipant, isInstructor);

      if (modifiedSessions?.length) {
        contentTypes = [...contentTypes, ...aditionalContentTypes];
        return updateSessionsBulk(modifiedSessions, contentTypes);
      }
      return false;
    }
  }

  const { modifiedSessions, aditionalContentTypes } = computeHorsesSessionsToUpdate([], sessions, horses, ownerParticipant, isInstructor);

  if (modifiedSessions?.length) {
    contentTypes = [...contentTypes, ...aditionalContentTypes];
    return updateSessionsBulk(modifiedSessions, contentTypes);
  }
  return false;
};

async function sessionsByRecurringUuid (recurringUuid, startTime, endTime, nextToken = null, results = []) {
  if (!recurringUuid) {
    return [];
  }

  const queryVariables = { recurring_uuid: recurringUuid };

  const filter = { workspace: { eq: store.getters.amplifyWorkspace } };

  if (startTime) {
    filter.start_time = { ge: startTime };
  }

  if (endTime) {
    filter.end_time = { le: endTime };
  }

  if (nextToken) {
    queryVariables.nextToken = nextToken;
  }

  return API.graphql(graphqlOperation(queries.sessionsByRecurringUuid, { ...queryVariables, filter: filter })).then(resp => {
    const retrievedSession = resp.data.sessionsByRecurringUuid.items.map(s => parseSessionJsonFields(s));
    retrievedSession.map(s => results.push(s));

    const queryNextToken = resp.data.sessionsByRecurringUuid.nextToken;
    if (queryNextToken) {
      return sessionsByRecurringUuid(recurringUuid, startTime, endTime, queryNextToken, results);
    }

    return results;
  });
}

async function removePreReservations (preReservationsToDelete) {
  let debugLog = null;

  const contentTypeUuids = (preReservationsToDelete || []).map(p => ({ id: p.reservationId }));

  if (store.state.debugMode) {
    debugLog = inicializeDebugLog({ contentTypeUuids, method: 'mutations.removePreReservations' });
  }

  await preReservationsDeletionDebug(preReservationsToDelete);

  return API.graphql(graphqlOperation(mutations.removePreReservations, { participants: contentTypeUuids }))
    .then(resp => {
      if (store.state.debugMode && debugLog) {
        debugLog.response = resp;
      }
      return resp.data.removePreReservations;
    })
    .catch(err => {
      if (store.state.debugMode) {
        debugLog.error = err;
      }
      console.log('ERR', err);
      return { success: false, message: 'Something went wrong!' };
    })
    .finally(() => {
      if (store.state.debugMode) {
        handleDebugLog(debugLog);
      }
    });
}

async function getSessionByContentUuid (content_type, content_type_uuid, workspace, start_time, end_time) {
  let params = {
    content_type_uuid: content_type_uuid,
    content_type: content_type,
    workspace: workspace.toUpperCase()
  };

  if (start_time) {
    params = {
      ...params,
      start_time: start_time
    };
  }

  if (end_time) {
    params = {
      ...params,
      end_time: end_time
    };
  }

  return API.graphql(graphqlOperation(queries.getSessionByContentUuid, { ...params })).then(resp => (resp.data.getSessionByContentUuid.items || []).map(s => parseSessionJsonFields(s))).catch(err => {
    console.log('ERR', err);
    return [];
  });
}

Hub.listen('api', data => {
  const { payload } = data;

  store.commit('setWebSocketError', null);
  let message = null;
  if (payload.event === 'ConnectionStateChange' && payload.data && payload.data.connectionState && !store.state.loading) {
    switch (payload.data.connectionState) {
      case 'ConnectedPendingNetwork':
      case 'ConnectionDisrupted':
      case 'ConnectionDisruptedPendingNetwork':
      case 'Disconnected': {
        message = subscriptionErrorMessage;
        break;
      }

      default:
        break;
    }
    store.commit('setWebSocketError', message);
  }
});

export {
  getSessionById,
  getSessionByContentUuid,
  parseSessionJsonFields,
  unparseSessionJsonFields,
  updateReplicaSessions,
  sessionsByRecurringUuid,
  addParticipantToSession,
  deleteContentsFromSession,
  getSameHoranetSessions,
  createSession,
  getRecurringSession,
  deleteSessionHelpers,
  getSessionsByPeriod,
  weeklySessions,
  contentTypeSessions,
  getAssociateRecurringSessions,
  getAssociateRecurringSessionsByDate,
  prepareSession,
  updateSessionPresence,
  updateSessionContentType,
  handleSessions,
  handleSubscription,
  listAmplifySessions,
  updateSessionAmplify,
  syncHoranetSessions,
  unsubscribe,
  removePreReservations,
  getSessionsByPeriodWithContentType,
  addInstructorsToSession,
  addHorsesToSession,
  openSearchCustomSessions,
  insertContentTypeSession,
  updateContentTypeSession,
  deleteContentTypeSession
};
